import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';

import { DatePicker } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { isJobEvent, isBookingEvent } from '@libs/EventTypes';
import { getMoment, toISODate } from '@libs/NwMoment';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import { getSendAgendaRange } from '@components/Schedule/ScheduleUtils';

import { PrintableDrawerWrapper, PrintableExportListWrapper } from './ExportListDrawerUI';
import ExportListEvent from './ExportListEvent';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { RangePicker } = DatePicker;

const StyledRangePicker = styled(RangePicker)`
    margin: auto 0;
    width: 100%;
    max-width: 20rem;
`;

const ExportListDrawer = ({ onClose, contactID, scheduleRange }) => {
    const componentRef = useRef();

    const sendAgendaRange = getSendAgendaRange(scheduleRange);

    const [exportStartDate, setExportStartDate] = useState(sendAgendaRange.start);
    const [exportEndDate, setExportEndDate] = useState(sendAgendaRange.end);
    const [dateEvents, setDateEvents] = useState([]);

    const [filters, setFilters] = useState({
        showCastings: true,
        showExtJobs: true,
        showAccommodations: true,
        showFollowUps: true,
        showTravels: true,
        showOptions: false,
        showCancelled: false,
        showEventNotes: true,
    });

    const handleExportPeriodChange = (dates) => {
        setExportStartDate(getMoment(dates[0]));
        setExportEndDate(getMoment(dates[1]));
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        bodyClass: 'print-body',
        documentTitle: 'Statement',
        onAfterPrint: () => {
            onClose();
        },
    });

    const getEvents = async () => {
        const postParams = {
            FDate: toISODate(exportStartDate),
            TDate: toISODate(exportEndDate),
            inclJobs: true,
            inclExtJobs: true,
            inclCastings: true,
            checkConflict: false,
            inclTravels: true,
            inclAccommodations: true,
            inclExternalJobs: true,
            inclFollowUps: true,
            inclMeetings: true,
            inclNotices: true,
            General: false,
            Warning: false,
            idmodels: [contactID],
        };
        const events = await Axios.post('/calendar/getevents', postParams);
        setDateEvents(events.Events);
    };

    useEffect(() => {
        getEvents();
    }, [exportStartDate, exportEndDate, contactID]);

    const filteredEvents = useMemo(() => {
        let filteredEvents = dateEvents;

        if (!filters.showOptions) {
            filteredEvents = filteredEvents.filter((event) => {
                return !(isJobEvent(event.TypeName) && event.Confirmed === 0);
            });
        }

        if (!filters.showAccommodations) {
            filteredEvents = filteredEvents.filter((event) => {
                return event.TypeName !== 'Accommodation';
            });
        }

        if (!filters.showCastings) {
            filteredEvents = filteredEvents.filter((event) => {
                return event.TypeName !== 'Casting';
            });
        }

        if (!filters.showFollowUps) {
            filteredEvents = filteredEvents.filter((event) => {
                return event.TypeName !== 'FollowUp';
            });
        }

        if (!filters.showTravels) {
            filteredEvents = filteredEvents.filter((event) => {
                return event.TypeName !== 'Travel';
            });
        }

        if (!filters.showExtJobs) {
            filteredEvents = filteredEvents.filter((event) => {
                return event.TypeName !== 'ExtJob';
            });
        }

        if (!filters.showCancelled) {
            filteredEvents = filteredEvents.filter((event) => {
                return !event.Cancel;
            });
        }

        return filteredEvents;
    }, [dateEvents, filters]);

    const eventsGroupedByDay = useMemo(() => {
        const groupedByMonth = {};

        // Convert export dates to moment objects for comparison
        const periodStart = getMoment(exportStartDate);
        const periodEnd = getMoment(exportEndDate);

        filteredEvents.forEach((event) => {
            // For events that span multiple days
            const startDate = getMoment(event.FromDate);
            const endDate = getMoment(event.ToDate);

            // If it's a multi-day event (and not a Casting or Job)
            if (!isBookingEvent(event.TypeName) && !startDate.isSame(endDate, 'day')) {
                // Create a copy of the start date to iterate through
                let currentDate = startDate.clone();

                // If event starts before the selected period, adjust currentDate to start at periodStart
                if (currentDate.isBefore(periodStart, 'day')) {
                    currentDate = periodStart.clone();
                }

                // Loop through each day in the range, but only until the end of the selected period
                const loopEndDate = endDate.isAfter(periodEnd) ? periodEnd.clone() : endDate.clone();

                while (currentDate.isSameOrBefore(loopEndDate, 'day')) {
                    const monthYear = currentDate.format('MMMM YYYY');
                    const dateStr = currentDate.format('YYYY-MM-DD');

                    if (!groupedByMonth[monthYear]) {
                        groupedByMonth[monthYear] = {
                            month: currentDate.format('MMMM'),
                            year: currentDate.year(),
                            dates: {},
                        };
                    }

                    if (!groupedByMonth[monthYear].dates[dateStr]) {
                        groupedByMonth[monthYear].dates[dateStr] = {
                            date: dateStr,
                            events: [],
                        };
                    }

                    // Add the event to this day
                    groupedByMonth[monthYear].dates[dateStr].events.push({
                        ...event,
                        isMultiDayEvent: true,
                        multiDayPosition: currentDate.isSame(startDate, 'day')
                            ? 'start'
                            : currentDate.isSame(endDate, 'day')
                              ? 'end'
                              : 'middle',
                    });

                    // Move to the next day
                    currentDate.add(1, 'day');
                }
            } else {
                // For single-day events, use the original logic
                const eventDate = startDate;

                // Only include events within the selected period
                if (eventDate.isBetween(periodStart, periodEnd, 'day', '[]')) {
                    const monthYear = eventDate.format('MMMM YYYY');
                    const dateStr = eventDate.format('YYYY-MM-DD');

                    if (!groupedByMonth[monthYear]) {
                        groupedByMonth[monthYear] = {
                            month: eventDate.format('MMMM'),
                            year: eventDate.year(),
                            dates: {},
                        };
                    }

                    if (!groupedByMonth[monthYear].dates[dateStr]) {
                        groupedByMonth[monthYear].dates[dateStr] = {
                            date: dateStr,
                            events: [],
                        };
                    }

                    groupedByMonth[monthYear].dates[dateStr].events.push(event);
                }
            }
        });

        return Object.values(groupedByMonth).map((monthData) => ({
            ...monthData,
            dates: Object.values(monthData.dates),
        }));
    }, [filteredEvents, exportStartDate, exportEndDate]);

    const handleFilterChange = (filterName) => (value) => {
        setFilters((prev) => ({
            ...prev,
            [filterName]: value,
        }));
    };

    // Memoized function to count events by type
    const eventCounts = useMemo(() => {
        const counts = {
            casting: 0,
            options: 0,
            extJob: 0,
            followUp: 0,
            accommodation: 0,
            travel: 0,
            cancelled: 0,
        };

        dateEvents.forEach((event) => {
            if (event.TypeName === 'Casting') {
                counts.casting++;
            } else if (isJobEvent(event.TypeName) && event.Confirmed === 0) {
                counts.options++;
            } else if (event.TypeName === 'ExtJob') {
                counts.extJob++;
            } else if (event.TypeName === 'FollowUp') {
                counts.followUp++;
            } else if (event.TypeName === 'Accommodation') {
                counts.accommodation++;
            } else if (event.TypeName === 'Travel') {
                counts.travel++;
            }
        });
        counts.cancelled = dateEvents.filter((event) => event.Cancel).length;

        return counts;
    }, [dateEvents]);

    return (
        <NwDrawer title="Export Events List" onClose={onClose} width="60vw" whiteBg={false}>
            <PrintableDrawerWrapper>
                <div className="printable-statement-header">
                    <div className="filters-line">
                        <StyledRangePicker
                            autoComplete="off"
                            format="D MMM YYYY"
                            value={[getMoment(exportStartDate), getMoment(exportEndDate)]}
                            onChange={handleExportPeriodChange}
                        />
                        <p>
                            {dateEvents.length} events found{' '}
                            {filteredEvents.length !== dateEvents.length && `(${filteredEvents.length} filtered)`}
                        </p>
                    </div>
                    <NwDivider dark>Filter Events</NwDivider>
                    <div className="filters-line">
                        <CustomSwitch
                            size="small"
                            value={filters.showCastings}
                            text={`show castings (${eventCounts.casting})`}
                            onChange={handleFilterChange('showCastings')}
                        />
                        <CustomSwitch
                            size="small"
                            value={filters.showOptions}
                            text={`show options (${eventCounts.options})`}
                            onChange={handleFilterChange('showOptions')}
                        />
                        <CustomSwitch
                            size="small"
                            value={filters.showExtJobs}
                            text={`show external jobs (${eventCounts.extJob})`}
                            onChange={handleFilterChange('showExtJobs')}
                        />
                        <CustomSwitch
                            size="small"
                            value={filters.showFollowUps}
                            text={`show follow ups (${eventCounts.followUp})`}
                            onChange={handleFilterChange('showFollowUps')}
                        />
                        <CustomSwitch
                            size="small"
                            value={filters.showAccommodations}
                            text={`show accommodations (${eventCounts.accommodation})`}
                            onChange={handleFilterChange('showAccommodations')}
                        />
                        <CustomSwitch
                            size="small"
                            value={filters.showTravels}
                            text={`show travels (${eventCounts.travel})`}
                            onChange={handleFilterChange('showTravels')}
                        />
                        <CustomSwitch
                            size="small"
                            value={filters.showCancelled}
                            text={`show cancelled events (${eventCounts.cancelled})`}
                            onChange={handleFilterChange('showCancelled')}
                        />
                    </div>
                    <NwDivider dark>Events content</NwDivider>
                    <div className="filters-line">
                        <CustomSwitch
                            size="small"
                            value={filters.showEventNotes}
                            text={`show event notes`}
                            onChange={handleFilterChange('showEventNotes')}
                        />
                    </div>
                    <div className="button-line">
                        <NwButton
                            size="big"
                            primary
                            label="Print"
                            icon={byPrefixAndName.fasl['print']}
                            onClick={handlePrint}
                        />
                    </div>
                </div>
                <div className="printable-content-wrapper">
                    <PrintableExportListWrapper ref={componentRef}>
                        {eventsGroupedByDay.map((monthData) => (
                            <div key={`${monthData.month}-${monthData.year}`} className="event-month">
                                <h2>
                                    {monthData.month} {monthData.year}
                                </h2>
                                <div className="event-month-days">
                                    {monthData.dates.map((dateData) => (
                                        <div key={dateData.date} className="event-day">
                                            <h3>{getMoment(dateData.date).format('D MMMM YYYY')}</h3>
                                            <div className="event-day-events">
                                                {dateData.events.map((event) => (
                                                    <ExportListEvent
                                                        key={event.ID}
                                                        event={event}
                                                        showNotes={filters.showEventNotes}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </PrintableExportListWrapper>
                </div>
            </PrintableDrawerWrapper>
        </NwDrawer>
    );
};

export default ExportListDrawer;
